import React, { useMemo } from "react";

import { SourceLocation } from "models";

import { Button, Icon, Modal, PdfDocumentViewer, Segmented, SourcePageViewer, buildSourcePageTabs } from "components";

export type DocScanDocumentView = "sourceRows" | "fullDocument";

const VIEW_OPTIONS: { value: DocScanDocumentView; label: string }[] = [
  { value: "sourceRows", label: "Source rows" },
  { value: "fullDocument", label: "Full document" },
];

type DocScanDocumentModalProps = {
  fileName: string | null;
  fileUrl: string;
  sourceLocations: SourceLocation[];
  isOpen: boolean;
  view: DocScanDocumentView;
  onViewChange: (view: DocScanDocumentView) => void;
  onClose: () => void;
};

/** Large view of the scanned PDF, either the framed rows doc-scan read the values from or every page. */
export const DocScanDocumentModal = ({ fileName, fileUrl, sourceLocations, isOpen, view, onViewChange, onClose }: DocScanDocumentModalProps) => {
  const hasSourceRows = sourceLocations.length > 0;
  // Every page opens on the page the source rows open on, so the admin keeps the match in view.
  const matchPageNumber = useMemo(() => buildSourcePageTabs(sourceLocations)[0]?.page, [sourceLocations]);

  return (
    <Modal
      destroyOnClose
      maskClosable
      showModal={isOpen}
      onCancel={onClose}
      closeIcon={<Icon iconName="tabler/x" />}
      title={fileName ?? "Scanned document"}
      wrapClassName="doc-scan-document-modal-wrap"
      footer={
        <div className="doc-scan-document-modal-footer">
          <Button
            type="secondary"
            className="doc-scan-document-modal-button"
            iconLeft
            icon={<Icon iconName="tabler/external-link" size="s" />}
            onClick={() => window.open(fileUrl, "_blank", "noopener,noreferrer")}
          >
            Open in new tab
          </Button>
          <Button type="primary" className="doc-scan-document-modal-button" onClick={onClose}>
            Close
          </Button>
        </div>
      }
      content={
        <div className="doc-scan-document-modal">
          {hasSourceRows && <Segmented<DocScanDocumentView> value={view} onChange={onViewChange} options={VIEW_OPTIONS} />}
          <div className="doc-scan-document-modal-body">
            {hasSourceRows && view === "sourceRows" ? (
              <SourcePageViewer pdfUrl={fileUrl} locations={sourceLocations} />
            ) : (
              <PdfDocumentViewer pdfUrl={fileUrl} initialPageNumber={matchPageNumber} />
            )}
          </div>
        </div>
      }
    />
  );
};
